import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  TablePagination,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { useStyles } from "./styles";

const COLUMNS = [
  { id: "name", label: "Name" },
  { id: "signupCode", label: "Signup Code" },
  { id: "emailDomain", label: "Email Domain" },
];

const RewardGroupsList = ({
  data,
  loading,
  error,
  pagination,
  onPaginationChange,
  onEdit,
  onDelete,
  onRetry,
}) => {
  const classes = useStyles();
  const [sortField, setSortField] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  const handleSortClick = (columnId) => {
    if (sortField === columnId) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(columnId);
      setSortDir("asc");
    }
  };

  const sortedCollection = useMemo(() => {
    if (!data?.collection?.length) return [];
    return [...data.collection].sort((a, b) => {
      const aVal = (a[sortField] || "").toLowerCase();
      const bVal = (b[sortField] || "").toLowerCase();
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDir]);

  const handleChangePage = (event, newPage) => {
    onPaginationChange({
      ...pagination,
      offset: newPage * pagination.limit,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    onPaginationChange({
      limit: parseInt(event.target.value, 10),
      offset: 0,
    });
  };

  const renderSortIcon = (columnId) => {
    if (sortField !== columnId) return null;
    return sortDir === "asc" ? (
      <ArrowUpwardIcon className={classes.sortIcon} />
    ) : (
      <ArrowDownwardIcon className={classes.sortIcon} />
    );
  };

  if (error) {
    return (
      <Box textAlign="center" py={6}>
        <Typography variant="body1" color="error" gutterBottom>
          Failed to load reward groups.
        </Typography>
        <Button variant="outlined" size="small" onClick={onRetry}>
          Retry
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <table className={classes.table}>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th key={col.id}>{col.label}</th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <tr key={i}>
              <td data-column="Name">
                <Skeleton variant="text" width="60%" />
              </td>
              <td data-column="Signup Code">
                <Skeleton variant="text" width="40%" />
              </td>
              <td data-column="Email Domain">
                <Skeleton variant="text" width="50%" />
              </td>
              <td data-column="Actions">
                <Skeleton variant="rect" width={48} height={28} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (!loading && data.totalCount === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" gutterBottom>
          No reward groups yet
        </Typography>
      </Box>
    );
  }

  const currentPage = Math.floor(pagination.offset / pagination.limit);

  return (
    <div className={classes.tableContainer}>
      <TablePagination
        component="div"
        count={data.totalCount}
        page={currentPage}
        onChangePage={handleChangePage}
        rowsPerPage={pagination.limit}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 15, 25, 50]}
        className={classes.paginationTop}
      />

      <table className={classes.table}>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th key={col.id} onClick={() => handleSortClick(col.id)}>
                {col.label}
                {renderSortIcon(col.id)}
              </th>
            ))}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sortedCollection.map((row) => (
            <tr key={row.rewardGroupID}>
              <td data-column="Name">{row.name}</td>
              <td data-column="Signup Code">{row.signupCode || "\u2014"}</td>
              <td data-column="Email Domain">{row.emailDomain || "\u2014"}</td>
              <td data-column="Actions">
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(row);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(row);
                  }}
                  className={classes.listDeleteBtn}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RewardGroupsList;
